import { supabase } from '../lib/supabase';

const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    acc[camelKey] = toCamel(obj?.[key]);
    return acc;
  }, {});
};

const toSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnake);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (l) => `_${l?.toLowerCase()}`);
    acc[snakeKey] = toSnake(obj?.[key]);
    return acc;
  }, {});
};

export const productCatalogService = {
  // ─── Products ───────────────────────────────────────────────────────────────
  async getProducts({ search = '', productType = '', category = '', brand = '', activeOnly = true } = {}) {
    try {
      let query = supabase?.from('products')?.select('*')?.order('name');
      if (activeOnly) query = query?.eq('active', true);
      if (productType) query = query?.eq('product_type', productType);
      if (category) query = query?.eq('category', category);
      if (brand) query = query?.ilike('brand', `%${brand}%`);
      if (search) {
        query = query?.or(`name.ilike.%${search}%,brand.ilike.%${search}%,category.ilike.%${search}%,sku.ilike.%${search}%`);
      }
      const { data, error } = await query;
      if (error) { console.error('Error fetching products:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      return [];
    }
  },

  async createProduct(productData) {
    try {
      const payload = toSnake(productData);
      const { data, error } = await supabase?.from('products')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to create product:', err);
      throw err;
    }
  },

  async updateProduct(id, updateData) {
    try {
      const { data, error } = await supabase?.from('products')?.update(toSnake(updateData))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to update product:', err);
      throw err;
    }
  },

  async deactivateProduct(id) {
    try {
      const { error } = await supabase?.from('products')?.update({ active: false })?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to deactivate product:', err);
      throw err;
    }
  },

  // ─── Supplier Products ───────────────────────────────────────────────────────
  async getSupplierProducts(productId) {
    try {
      const { data, error } = await supabase
        ?.from('supplier_products')
        ?.select('*, provider:providers(id, name, phone, whatsapp, email)')
        ?.eq('product_id', productId)
        ?.eq('active', true)
        ?.order('last_update', { ascending: false });
      if (error) { console.error('Error fetching supplier products:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch supplier products:', err);
      return [];
    }
  },

  async upsertSupplierProduct(data) {
    try {
      const payload = {
        provider_id: data?.providerId,
        product_id: data?.productId,
        supplier_sku: data?.supplierSku || null,
        cost_price: data?.costPrice || 0,
        available_stock: data?.availableStock || null,
        lead_time_days: data?.leadTimeDays || null,
        last_update: new Date()?.toISOString(),
        active: true,
      };
      const { data: result, error } = await supabase
        ?.from('supplier_products')
        ?.upsert(payload, { onConflict: 'provider_id,product_id' })
        ?.select()?.single();
      if (error) throw error;
      return toCamel(result);
    } catch (err) {
      console.error('Failed to upsert supplier product:', err);
      throw err;
    }
  },

  async removeSupplierProduct(id) {
    try {
      const { error } = await supabase?.from('supplier_products')?.update({ active: false })?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to remove supplier product:', err);
      throw err;
    }
  },

  // ─── Vehicle Compatibility ───────────────────────────────────────────────────
  async getCompatibility(productId) {
    try {
      const { data, error } = await supabase
        ?.from('vehicle_compatibility')
        ?.select('*')
        ?.eq('product_id', productId)
        ?.order('make');
      if (error) { console.error('Error fetching compatibility:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch compatibility:', err);
      return [];
    }
  },

  async addCompatibility(productId, compatData) {
    try {
      const payload = toSnake({ ...compatData, productId });
      const { data, error } = await supabase?.from('vehicle_compatibility')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to add compatibility:', err);
      throw err;
    }
  },

  async deleteCompatibility(id) {
    try {
      const { error } = await supabase?.from('vehicle_compatibility')?.delete()?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to delete compatibility:', err);
      throw err;
    }
  },

  // ─── Providers (reuse existing table) ───────────────────────────────────────
  async getProviders() {
    try {
      const { data, error } = await supabase
        ?.from('providers')
        ?.select('id, name, phone, whatsapp, email, contact_name, active')
        ?.eq('active', true)
        ?.order('name');
      if (error) { console.error('Error fetching providers:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      return [];
    }
  },

  // ─── CSV Import ──────────────────────────────────────────────────────────────
  async importFromCSV(rows) {
    const summary = { productsCreated: 0, productsUpdated: 0, providersCreated: 0, errors: [] };

    for (let i = 0; i < rows?.length; i++) {
      const row = rows?.[i];
      try {
        // 1) Upsert product
        const productKey = row?.sku ? { sku: row?.sku } : null;
        let product = null;

        if (productKey?.sku) {
          const { data: existing } = await supabase?.from('products')?.select('id')?.eq('sku', row?.sku)?.maybeSingle();
          if (existing) {
            const { data: updated } = await supabase?.from('products')?.update({
              product_type: row?.product_type || null,
              category: row?.category || null,
              subcategory: row?.subcategory || null,
              brand: row?.brand || null,
              name: row?.name,
              description: row?.description || null,
              unit: row?.unit || 'pieza',
              sale_price: parseFloat(row?.sale_price) || 0,
            })?.eq('id', existing?.id)?.select()?.single();
            product = updated;
            summary.productsUpdated++;
          } else {
            const { data: created } = await supabase?.from('products')?.insert({
              sku: row?.sku || null,
              product_type: row?.product_type || null,
              category: row?.category || null,
              subcategory: row?.subcategory || null,
              brand: row?.brand || null,
              name: row?.name,
              description: row?.description || null,
              unit: row?.unit || 'pieza',
              sale_price: parseFloat(row?.sale_price) || 0,
            })?.select()?.single();
            product = created;
            summary.productsCreated++;
          }
        } else {
          // No SKU: match by brand+name
          const { data: existing } = await supabase?.from('products')?.select('id')
            ?.eq('brand', row?.brand || '')
            ?.eq('name', row?.name || '')
            ?.maybeSingle();
          if (existing) {
            const { data: updated } = await supabase?.from('products')?.update({
              product_type: row?.product_type || null,
              category: row?.category || null,
              subcategory: row?.subcategory || null,
              unit: row?.unit || 'pieza',
              sale_price: parseFloat(row?.sale_price) || 0,
            })?.eq('id', existing?.id)?.select()?.single();
            product = updated;
            summary.productsUpdated++;
          } else {
            const { data: created } = await supabase?.from('products')?.insert({
              sku: null,
              product_type: row?.product_type || null,
              category: row?.category || null,
              subcategory: row?.subcategory || null,
              brand: row?.brand || null,
              name: row?.name,
              description: row?.description || null,
              unit: row?.unit || 'pieza',
              sale_price: parseFloat(row?.sale_price) || 0,
            })?.select()?.single();
            product = created;
            summary.productsCreated++;
          }
        }

        if (!product?.id) continue;

        // 2) Upsert provider (from existing providers table)
        if (row?.provider_name) {
          let provider = null;
          const { data: existingProvider } = await supabase?.from('providers')?.select('id')?.ilike('name', row?.provider_name)?.maybeSingle();
          if (existingProvider) {
            provider = existingProvider;
          } else {
            const { data: newProvider } = await supabase?.from('providers')?.insert({ name: row?.provider_name, active: true })?.select()?.single();
            provider = newProvider;
            summary.providersCreated++;
          }

          // 3) Upsert supplier_products
          if (provider?.id) {
            await supabase?.from('supplier_products')?.upsert({
              provider_id: provider?.id,
              product_id: product?.id,
              supplier_sku: row?.supplier_sku || null,
              cost_price: parseFloat(row?.cost_price) || 0,
              available_stock: row?.available_stock ? parseFloat(row?.available_stock) : null,
              lead_time_days: row?.lead_time_days ? parseFloat(row?.lead_time_days) : null,
              last_update: new Date()?.toISOString(),
              active: true,
            }, { onConflict: 'provider_id,product_id' });
          }
        }

        // 4) Upsert vehicle_compatibility
        if (row?.make && row?.model) {
          const { data: existingCompat } = await supabase?.from('vehicle_compatibility')?.select('id')
            ?.eq('product_id', product?.id)
            ?.eq('make', row?.make)
            ?.eq('model', row?.model)
            ?.maybeSingle();
          if (!existingCompat) {
            await supabase?.from('vehicle_compatibility')?.insert({
              product_id: product?.id,
              make: row?.make,
              model: row?.model,
              year_from: row?.year_from ? parseFloat(row?.year_from) : null,
              year_to: row?.year_to ? parseFloat(row?.year_to) : null,
              engine: row?.engine || null,
              notes: row?.notes || null,
            });
          }
        }
      } catch (err) {
        summary?.errors?.push(`Fila ${i + 2}: ${err?.message || 'Error desconocido'}`);
      }
    }

    return summary;
  },
};

export default productCatalogService;
